/** WebMCP Control Panel view (View 15). */

import { useWebMcp } from "@gotts.ai/mcp/browser";
import { TOOL_CATALOG } from "@gotts.ai/tools/tool-catalog";
import { Toggle } from "@gotts.ai/ui";
import { CollapsibleSection } from "@gotts.ai/ui/devtools/components/CollapsibleSection";
import { PcbCard } from "@gotts.ai/ui/devtools/components/PcbCard";
import { useEffect, useMemo, useState } from "react";

import { CATEGORY_LABELS, PROFILE_CATEGORIES } from "../webmcp/profiles.js";
import { CHAIN_CONTROL_TOOLS } from "../webmcp/tools/chain-controls.js";
import { ENVIRONMENT_TOOLS } from "../webmcp/tools/environment.js";
import { SCENARIO_TOOLS } from "../webmcp/tools/scenarios.js";
import { STATE_TOOLS } from "../webmcp/tools/state.js";

import type { WebMcpLogEntry, WebMcpProfile } from "@gotts.ai/mcp";

interface CatalogEntry {
  name: string;
  description: string;
  category: string;
  source: "safe" | "webenv";
}

/** Pre-built full catalog of all tools (safe + webenv). */
const FULL_CATALOG: CatalogEntry[] = [
  ...TOOL_CATALOG.map((t) => ({
    name: t.name,
    description: t.description,
    category: t.category,
    source: "safe" as const,
  })),
  ...ENVIRONMENT_TOOLS.map((t) => ({
    name: t.name,
    description: t.description,
    category: "environment",
    source: "webenv" as const,
  })),
  ...CHAIN_CONTROL_TOOLS.map((t) => ({
    name: t.name,
    description: t.description,
    category: "chain-controls",
    source: "webenv" as const,
  })),
  ...STATE_TOOLS.map((t) => ({
    name: t.name,
    description: t.description,
    category: "state",
    source: "webenv" as const,
  })),
  ...SCENARIO_TOOLS.map((t) => ({
    name: t.name,
    description: t.description,
    category: "scenarios",
    source: "webenv" as const,
  })),
];

const PROFILES: { value: WebMcpProfile; label: string; description: string }[] =
  [
    {
      value: "webenv-data",
      label: "Data",
      description: "Read-only data tools (~50)",
    },
    {
      value: "webenv-trader",
      label: "Trader",
      description: "Data + trading + safety (~75)",
    },
    {
      value: "webenv-lp",
      label: "LP",
      description: "Trader + LP + scenarios (~90)",
    },
    {
      value: "webenv-full",
      label: "Full",
      description: "All tools except stubs (~128)",
    },
    {
      value: "webenv-dev",
      label: "Dev",
      description: "All tools including stubs (~138)",
    },
  ];

function StatusBadge({ connected }: { connected: boolean }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${connected ? "bg-[var(--color-success)]" : "bg-[var(--color-error)]"}`}
    />
  );
}

function LogEntry({ entry }: { entry: WebMcpLogEntry }) {
  const time = new Date(entry.timestamp).toLocaleTimeString();
  const isError = entry.statusCode === "error";
  return (
    <div className="border-b border-[var(--glass-border)] py-0.5 font-mono text-xs">
      <span className="text-[var(--color-text-muted)]">{time}</span>{" "}
      <span className="text-[var(--color-text-dim)]">[{entry.clientId}]</span>{" "}
      <span
        className={
          isError ? "text-[var(--color-error)]" : "text-[var(--color-info)]"
        }
      >
        {entry.toolName}
      </span>{" "}
      <span className="text-[var(--color-text-muted)]">
        {entry.latencyMs}ms
      </span>
    </div>
  );
}

function ConnectModal({
  onClose,
  wsUrl,
  httpUrl,
}: {
  onClose: () => void;
  wsUrl: string;
  httpUrl: string;
}) {
  const [tab, setTab] = useState<
    "claude-desktop" | "claude-code" | "cursor" | "custom"
  >("claude-desktop");

  const sseUrl = httpUrl.replace(/\/mcp$/, "/sse");

  const claudeDesktopConfig = JSON.stringify(
    { mcpServers: { webenv: { type: "sse", url: sseUrl } } },
    null,
    2,
  );

  const claudeCodeConfig = JSON.stringify(
    { mcpServers: { webenv: { type: "sse", url: sseUrl } } },
    null,
    2,
  );

  const cursorConfig = JSON.stringify(
    { mcp: { servers: { webenv: { type: "sse", url: sseUrl } } } },
    null,
    2,
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="w-[90%] max-w-xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <PcbCard className="max-h-[80vh] overflow-auto">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="type-section-title text-lg">Connect Your Agent</h2>
            <button
              onClick={onClose}
              className="text-lg text-[var(--color-text-muted)] hover:text-[var(--color-text-dim)]"
            >
              x
            </button>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {(
              ["claude-desktop", "claude-code", "cursor", "custom"] as const
            ).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                }}
                className={`chip-preset ${
                  tab === t
                    ? "!border-[var(--color-gold)] !bg-[rgba(201,168,76,0.12)] !text-[var(--color-gold)]"
                    : ""
                }`}
              >
                {t === "claude-desktop"
                  ? "Claude Desktop"
                  : t === "claude-code"
                    ? "Claude Code"
                    : t === "cursor"
                      ? "Cursor / VS Code"
                      : "Custom"}
              </button>
            ))}
          </div>

          {tab === "claude-desktop" && (
            <div>
              <p className="mb-2 text-sm text-[var(--color-text-dim)]">
                Add this to your <code>claude_desktop_config.json</code>:
              </p>
              <pre className="overflow-auto rounded-md border border-[var(--glass-border)] bg-[var(--color-black)] p-3 font-mono text-xs text-[var(--color-text-dim)]">
                {claudeDesktopConfig}
              </pre>
              <button
                onClick={() =>
                  navigator.clipboard.writeText(claudeDesktopConfig)
                }
                className="btn-ghost mt-2 rounded-md px-3 py-1.5"
              >
                Copy to Clipboard
              </button>
            </div>
          )}

          {tab === "claude-code" && (
            <div>
              <p className="mb-2 text-sm text-[var(--color-text-dim)]">
                Add this to your project <code>.mcp.json</code>:
              </p>
              <pre className="overflow-auto rounded-md border border-[var(--glass-border)] bg-[var(--color-black)] p-3 font-mono text-xs text-[var(--color-text-dim)]">
                {claudeCodeConfig}
              </pre>
              <button
                onClick={() => navigator.clipboard.writeText(claudeCodeConfig)}
                className="btn-ghost mt-2 rounded-md px-3 py-1.5"
              >
                Copy to Clipboard
              </button>
            </div>
          )}

          {tab === "cursor" && (
            <div>
              <p className="mb-2 text-sm text-[var(--color-text-dim)]">
                Add this to your editor's MCP settings:
              </p>
              <pre className="overflow-auto rounded-md border border-[var(--glass-border)] bg-[var(--color-black)] p-3 font-mono text-xs text-[var(--color-text-dim)]">
                {cursorConfig}
              </pre>
              <button
                onClick={() => navigator.clipboard.writeText(cursorConfig)}
                className="btn-ghost mt-2 rounded-md px-3 py-1.5"
              >
                Copy to Clipboard
              </button>
            </div>
          )}

          {tab === "custom" && (
            <div className="text-sm text-[var(--color-text-dim)]">
              <p>Connection endpoints:</p>
              <div className="mt-2 space-y-2">
                <div>
                  <strong>WebSocket:</strong>{" "}
                  <code className="text-[var(--color-info)]">{wsUrl}</code>
                </div>
                <div>
                  <strong>HTTP (SSE):</strong>{" "}
                  <code className="text-[var(--color-info)]">{httpUrl}</code>
                </div>
                <p className="mt-3 text-[var(--color-text-muted)]">
                  Both endpoints accept JSON-RPC 2.0 requests with MCP protocol
                  methods (tools/list, tools/call).
                </p>
              </div>
            </div>
          )}
        </PcbCard>
      </div>
    </div>
  );
}

function ToolCatalog({ profile }: { profile: WebMcpProfile }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const allowed = PROFILE_CATEGORIES[profile];
    const q = search.toLowerCase();
    return FULL_CATALOG.filter(
      (t) =>
        allowed.has(t.category) &&
        (q === "" ||
          t.name.includes(q) ||
          t.description.toLowerCase().includes(q)),
    );
  }, [profile, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogEntry[]>();
    for (const t of filtered) {
      let arr = map.get(t.category);
      if (!arr) {
        arr = [];
        map.set(t.category, arr);
      }
      arr.push(t);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <PcbCard
      title="Tool Catalog"
      headerRight={
        <span className="text-xs text-[var(--color-text-muted)]">
          {filtered.length} tools
        </span>
      }
    >
      <input
        type="text"
        placeholder="Search tools..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        className="mb-3 w-full rounded-md border border-[var(--glass-border)] bg-[var(--color-black)] px-3 py-1.5 font-mono text-xs text-[var(--color-text-dim)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-gold)] focus:outline-none"
      />
      <div className="max-h-[500px] overflow-auto">
        {grouped.length === 0 ? (
          <div className="text-xs italic text-[var(--color-text-muted)]">
            No tools match your search
          </div>
        ) : (
          grouped.map(([category, tools]) => (
            <CollapsibleSection
              key={category}
              title={CATEGORY_LABELS[category] ?? category}
              badge={String(tools.length)}
              defaultOpen={false}
              className="mb-1"
            >
              <div className="space-y-1.5 py-1">
                {tools.map((t) => (
                  <div key={t.name}>
                    <span className="font-mono text-xs text-[var(--color-info)]">
                      {t.name}
                    </span>
                    {t.source === "webenv" && (
                      <span className="ml-1.5 rounded-sm bg-[rgba(201,168,76,0.15)] px-1 py-0.5 text-[9px] text-[var(--color-gold)]">
                        webenv
                      </span>
                    )}
                    <div className="text-[11px] text-[var(--color-text-muted)]">
                      {t.description}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          ))
        )}
      </div>
    </PcbCard>
  );
}

export default function WebMcpPanel() {
  const webmcp = useWebMcp();
  const [logEntries, setLogEntries] = useState<WebMcpLogEntry[]>([]);
  const [showConnectModal, setShowConnectModal] = useState(false);

  // Poll log entries
  useEffect(() => {
    if (!webmcp?.isEnabled) return;
    const interval = setInterval(() => {
      setLogEntries(webmcp.getLogEntries(100));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [webmcp?.isEnabled]);

  if (!webmcp) {
    return (
      <div className="text-[var(--color-text-muted)]">
        WebMCP is not available. Enable it in settings to expose tools via
        WebSocket/HTTP.
      </div>
    );
  }

  const counts = webmcp.isEnabled
    ? webmcp.getToolCounts()
    : { safe: 0, webenv: 0, total: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="type-section-title">WebMCP Control Panel</h1>
        <Toggle
          checked={webmcp.isEnabled}
          onCheckedChange={(v: boolean) => {
            webmcp.setEnabled(v);
          }}
          label={webmcp.isEnabled ? "Enabled" : "Disabled"}
        />
      </div>

      {webmcp.isEnabled && (
        <>
          {/* Connect button */}
          <button
            onClick={() => {
              setShowConnectModal(true);
            }}
            className="btn-primary w-full rounded-lg py-2.5"
          >
            Connect Your Agent
          </button>

          {/* Connection URLs */}
          <PcbCard title="Connection">
            <div className="space-y-1 text-xs text-[var(--color-text-dim)]">
              <div className="flex items-center gap-1.5">
                <StatusBadge connected /> WebSocket:{" "}
                <code className="text-[var(--color-info)]">{webmcp.wsUrl}</code>
              </div>
              <div className="flex items-center gap-1.5">
                <StatusBadge connected /> HTTP:{" "}
                <code className="text-[var(--color-info)]">
                  {webmcp.httpUrl}
                </code>
              </div>
            </div>
          </PcbCard>

          {/* Profile selector */}
          <PcbCard title="Tool Profile">
            <select
              value={webmcp.profile}
              onChange={(e) => {
                webmcp.setProfile(e.target.value as WebMcpProfile);
              }}
              className="w-full rounded-md border border-[var(--glass-border)] bg-[var(--color-black)] px-3 py-2 text-sm text-[var(--color-text-dim)]"
            >
              {PROFILES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label} — {p.description}
                </option>
              ))}
            </select>
          </PcbCard>

          {/* Tool counts */}
          <PcbCard>
            <div className="flex flex-wrap gap-3 sm:gap-6">
              <div className="text-center">
                <div className="stat-number text-2xl">{counts.total}</div>
                <div className="hero-stat-label">Total</div>
              </div>
              <div className="text-center">
                <div className="stat-number text-2xl">{counts.safe}</div>
                <div className="hero-stat-label">Safe</div>
              </div>
              <div className="text-center">
                <div className="stat-number text-2xl">{counts.webenv}</div>
                <div className="hero-stat-label">Webenv</div>
              </div>
            </div>
          </PcbCard>

          {/* Tool catalog */}
          <ToolCatalog profile={webmcp.profile} />

          {/* Activity log */}
          <PcbCard title="Activity Log">
            <div className="mb-2 flex items-center justify-between">
              <div />
              <button
                onClick={() => {
                  webmcp.clearLog();
                  setLogEntries([]);
                }}
                className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-dim)]"
              >
                Clear
              </button>
            </div>
            <div className="max-h-[300px] overflow-auto">
              {logEntries.length === 0 ? (
                <div className="text-xs italic text-[var(--color-text-muted)]">
                  No tool calls recorded yet
                </div>
              ) : (
                [...logEntries]
                  .reverse()
                  .map((entry, i) => <LogEntry key={i} entry={entry} />)
              )}
            </div>
          </PcbCard>

          {showConnectModal && (
            <ConnectModal
              onClose={() => {
                setShowConnectModal(false);
              }}
              wsUrl={webmcp.wsUrl}
              httpUrl={webmcp.httpUrl}
            />
          )}
        </>
      )}
    </div>
  );
}
